import { jsx as _jsx } from "react/jsx-runtime";
import MuiAutocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import * as React from 'react';
import { useClickAway, useId, useInitialShrink } from '../hooks';
import { IconArrowDropDown } from '../icon';
import { sentence } from '../typography';
import { deepMerge, withVersion } from '../utils';
import { useListBox } from './components/listbox';
import multiSelection from './components/multi-selection';
import option from './components/option';
import paper from './components/paper';
import popper from './components/popper';
import tags from './components/tags';
import textfield from './components/textfield';
export { SearchAutocomplete } from './search';
export { FacilityAutocomplete } from './facility';
const { useMultiselection } = multiSelection;
const { PaperComponent } = paper;
const { PopperComponent } = popper;
function AutocompleteImpl(props) {
    const [state, setState] = React.useState('closed');
    const ownId = useId();
    const { 
    // NOTE: we are not using these props, but we need to remove them from the rest
    autoFocus, label, placeholder, required, error, helperText, disableSearch, inputRef, bgColor, renderInput, skeleton, getOptionColor, getOptionIcon, renderOption, multipleConfig, optionHeight, onChange, value = null, onOpen, onClose, slots, slotProps, popperHeader, popperHeaderProps, menuTheme, loading, sentenceOptions = false, getOptionLabel: orgOptionLabel, open, ...muiProps } = props;
    const typedValue = value;
    const getOptionLabel = (option) => sentenceOptions
        ? sentence(orgOptionLabel?.(option) ?? String(option))
        : (orgOptionLabel?.(option) ?? String(option));
    const [internalInputValue, setInternalInputValue] = React.useState('');
    const [defaultFilterOptions] = React.useState(() => createFilterOptions());
    const { MultiSelection, multiSelectionProps } = useMultiselection(props);
    const shrink = useInitialShrink(typedValue, loading);
    const theme = useTheme();
    React.useEffect(() => {
        if (state === 'blocked') {
            // eslint-disable-next-line ml-rules/prefer-using-for-disposable
            const timeout = setTimeout(() => {
                setState('closed');
                onClose?.('toggleInput');
            }, 16 * 8);
            return () => clearTimeout(timeout);
        }
        return;
    }, [onClose, state]);
    React.useEffect(() => {
        if (open === undefined) {
            return;
        }
        if (open && state === 'closed') {
            setState('open');
        }
        else if (!open && state === 'open') {
            setState('closed');
        }
    }, [open, state]);
    const { id: ownPopperId, ref } = useClickAway(state === 'open', () => {
        setState('closed');
        onClose?.('blur');
    });
    const filteredOptions = (props.filterOptions ?? defaultFilterOptions)(props.options, {
        inputValue: props.inputValue ?? internalInputValue,
        getOptionLabel: props.getOptionLabel ?? ((option) => String(option))
    });
    const listBox = useListBox(state, optionHeight, filteredOptions);
    return (_jsx(MuiAutocomplete, { ref: ref, fullWidth: true, size: "small", id: ownId, open: state === 'open', onOpen: () => {
            if (state === 'closed') {
                setState('open');
                listBox.onOpen(value);
                onOpen?.();
            }
        }, onClose: (_e, reason) => {
            if (reason === 'blur') {
                return;
            }
            if (state === 'open') {
                setState('closed');
                onClose?.(reason);
            }
        }, value: typedValue, slots: {
            popper: PopperComponent,
            paper: PaperComponent,
            listbox: listBox.component,
            ...slots
        }, slotProps: {
            ...slotProps,
            // this is a wrokaround to pass custom-props to the PaperComponent
            // @see https://github.com/mui/material-ui/issues/31073
            // @see https://github.com/mui/material-ui/issues/38049
            // @see https://github.com/mui/material-ui/issues/39202
            paper: {
                ...slotProps?.paper,
                ref: listBox.paperRef,
                menuTheme,
                value: typedValue,
                PopperHeader: MultiSelection ?? popperHeader,
                popperHeaderProps: multiSelectionProps ?? popperHeaderProps
            },
            popper: {
                ...slotProps?.popper,
                ownPopperId
            },
            listbox: deepMerge(listBox.slotProps, slotProps?.listbox ?? {}, {
                sx: {
                    transition: 'max-height 0.2s',
                    boxSizing: 'border-box',
                    overscrollBehavior: 'contain',
                    scrollbarGutter: 'stable'
                }
            })
        }, renderValue: props.multiple
            ? (values, getItemProps) => {
                return tags.renderValue(values, getItemProps, props);
            }
            : undefined, renderOption: option.renderOption(props), popupIcon: _jsx(IconArrowDropDown, { ...slotProps?.popupIndicator, filled: state === 'open' }), getOptionLabel: getOptionLabel, ...muiProps, renderInput: (params) => {
            return textfield.renderInput(params, props, {
                theme,
                state,
                setState,
                shrink
            });
        }, onInputChange: (e, value, reason) => {
            setInternalInputValue(value);
            props.onInputChange?.(e, value, reason);
        }, onHighlightChange: (e, option, reason) => {
            listBox.onHighlightChange(e, option);
            props.onHighlightChange?.(e, option, reason);
        }, onChange: (e, value, reason) => onChange?.(value, e, reason) }));
}
export const Autocomplete = withVersion(AutocompleteImpl, '1.0.0');
